import { useState, useEffect, useCallback } from 'react';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Users,
  Plus,
  Trash2,
  Upload,
  ChevronRight,
  ChevronDown,
  Mail,
  X,
  Loader2,
  Pencil,
  Check,
  UserMinus,
  UserPlus,
  FileSpreadsheet,
  ArrowRight,
  Search,
  CheckSquare,
  Square,
  AlertTriangle,
  ToggleLeft,
  ToggleRight,
  Copy
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FIELD_OPTIONS = [
  { value: 'email', label: 'E-Mail' },
  { value: 'first_name', label: 'Vorname' },
  { value: 'last_name', label: 'Nachname' },
  { value: 'ignore', label: '— Ignorieren —' },
];

function detectDelimiter(text) {
  const first = text.split('\n')[0] || '';
  if (first.includes('\t')) return '\t';
  if (first.includes(';')) return ';';
  return ',';
}

function parseCSV(text, delimiter) {
  const rows = [];
  let current = '';
  let inQuotes = false;
  for (const ch of text) {
    if (ch === '"') { inQuotes = !inQuotes; continue; }
    if (ch === '\n' && !inQuotes) {
      rows.push(current.split(delimiter).map(c => c.trim()));
      current = '';
      continue;
    }
    if (ch === '\r') continue;
    current += ch;
  }
  if (current.trim()) rows.push(current.split(delimiter).map(c => c.trim()));
  return rows.filter(r => r.some(c => c));
}

function autoDetectMapping(rows) {
  if (!rows.length) return { mapping: [], hasHeader: false };
  const header = rows[0].map(h => h.toLowerCase().replace(/[-_ ]/g, ''));
  const emailKw = ['email', 'epost', 'mail', 'emailaddress', 'adresse', 'emailadresse'];
  const fnKw = ['vorname', 'firstname', 'first', 'prenom', 'givenname'];
  const lnKw = ['nachname', 'lastname', 'last', 'name', 'nom', 'familyname', 'surname'];
  const mapping = header.map(() => 'ignore');
  let hasHeader = false;
  for (let i = 0; i < header.length; i++) {
    if (emailKw.includes(header[i])) { mapping[i] = 'email'; hasHeader = true; }
    else if (fnKw.includes(header[i])) { mapping[i] = 'first_name'; hasHeader = true; }
    else if (lnKw.includes(header[i])) { mapping[i] = 'last_name'; hasHeader = true; }
  }
  if (!hasHeader) {
    for (let i = 0; i < rows[0].length; i++) {
      if (/[^@\s]+@[^@\s]+\.[^@\s]+/.test(rows[0][i])) { mapping[i] = 'email'; break; }
    }
  }
  if (!mapping.includes('email') && mapping.length > 0) mapping[0] = 'email';
  return { mapping, hasHeader };
}

function CsvPreviewDialog({ file, groupId, onClose, onImported }) {
  const [rows, setRows] = useState([]);
  const [mapping, setMapping] = useState([]);
  const [hasHeader, setHasHeader] = useState(false);
  const [importing, setImporting] = useState(false);
  const getToken = () => localStorage.getItem('shrt_token');

  useEffect(() => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const delim = detectDelimiter(text);
      const parsed = parseCSV(text, delim);
      setRows(parsed);
      const { mapping: m, hasHeader: h } = autoDetectMapping(parsed);
      setMapping(m);
      setHasHeader(h);
    };
    reader.readAsText(file, 'utf-8');
  }, [file]);

  const updateMapping = (colIdx, value) => {
    setMapping(prev => {
      const next = [...prev];
      const existing = next.indexOf(value);
      if (value !== 'ignore' && existing !== -1 && existing !== colIdx) next[existing] = 'ignore';
      next[colIdx] = value;
      return next;
    });
  };

  const emailColIdx = mapping.indexOf('email');
  const dataRows = hasHeader ? rows.slice(1) : rows;
  const previewRows = rows.slice(0, Math.min(6, rows.length));
  const totalDataRows = dataRows.length;

  const doImport = async () => {
    if (emailColIdx === -1) { toast.error('Bitte E-Mail-Spalte zuordnen'); return; }
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const fnCol = mapping.indexOf('first_name');
      const lnCol = mapping.indexOf('last_name');
      const params = new URLSearchParams();
      params.set('email_col', emailColIdx);
      if (fnCol !== -1) params.set('firstname_col', fnCol);
      if (lnCol !== -1) params.set('lastname_col', lnCol);
      params.set('skip_header', hasHeader);
      const res = await axios.post(
        `${API_URL}/api/admin/newsletter-groups/${groupId}/import?${params.toString()}`,
        formData,
        { headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'multipart/form-data' } }
      );
      toast.success(res.data.message);
      onImported();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import fehlgeschlagen');
    } finally { setImporting(false); }
  };

  if (!rows.length) return null;

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-card border border-border max-w-4xl w-full max-h-[80vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-testid="csv-preview-dialog">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center gap-2">
            <FileSpreadsheet size={20} className="text-primary" />
            <h3 className="font-semibold text-sm uppercase tracking-wider">CSV-Vorschau</h3>
            <span className="text-xs text-muted-foreground ml-2">{totalDataRows} Zeilen erkannt</span>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X size={18} /></button>
        </div>
        <div className="px-4 py-2 border-b border-border/50 flex items-center gap-3 bg-muted/20">
          <label className="flex items-center gap-2 text-xs cursor-pointer">
            <input type="checkbox" checked={hasHeader} onChange={e => setHasHeader(e.target.checked)} className="rounded" />
            <span>Erste Zeile ist Kopfzeile</span>
          </label>
        </div>
        <div className="overflow-x-auto border-b border-border">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/30">
                {previewRows[0]?.map((_, colIdx) => (
                  <th key={colIdx} className="p-2 text-left min-w-[140px]">
                    <select value={mapping[colIdx] || 'ignore'} onChange={e => updateMapping(colIdx, e.target.value)} className="w-full bg-background border border-border text-xs p-1.5 rounded-none text-foreground" data-testid={`col-mapping-${colIdx}`}>
                      {FIELD_OPTIONS.map(opt => (<option key={opt.value} value={opt.value}>{opt.label}</option>))}
                    </select>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {previewRows.map((row, rowIdx) => (
                <tr key={rowIdx} className={`border-t border-border/30 ${rowIdx === 0 && hasHeader ? 'bg-muted/20 text-muted-foreground italic' : ''}`}>
                  {row.map((cell, colIdx) => (
                    <td key={colIdx} className="p-2 text-xs truncate max-w-[200px]" title={cell}>{cell || <span className="text-muted-foreground/50">—</span>}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="text-xs text-muted-foreground">
            {emailColIdx === -1 ? (
              <span className="text-red-400">Bitte E-Mail-Spalte zuordnen</span>
            ) : (
              <span className="text-green-400">E-Mail: Spalte {emailColIdx + 1}{mapping.includes('first_name') && `, Vorname: Spalte ${mapping.indexOf('first_name') + 1}`}{mapping.includes('last_name') && `, Nachname: Spalte ${mapping.indexOf('last_name') + 1}`}</span>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onClose} className="rounded-none text-xs uppercase">Abbrechen</Button>
            <Button size="sm" onClick={doImport} disabled={importing || emailColIdx === -1} className="rounded-none text-xs uppercase bg-primary text-white gap-1" data-testid="csv-import-btn">
              {importing ? <Loader2 size={14} className="animate-spin" /> : <ArrowRight size={14} />}
              {importing ? 'Importiere...' : `${totalDataRows} Kontakte importieren`}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ContactTable({ contacts, loading, groupId, isSystem, onToggle, onDelete, onRefresh, allGroupContacts }) {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(new Set());

  // Reset selection when contacts change
  useEffect(() => { setSelected(new Set()); }, [contacts]);

  // Build duplicate map
  const duplicateMap = {};
  if (allGroupContacts) {
    for (const [gId, gContacts] of Object.entries(allGroupContacts)) {
      if (gId === groupId) continue;
      for (const c of gContacts) {
        const email = (c.email || '').toLowerCase();
        if (!duplicateMap[email]) duplicateMap[email] = [];
        duplicateMap[email].push(c.groupName || gId);
      }
    }
  }

  const filtered = contacts.filter(c => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (c.email || '').toLowerCase().includes(s) ||
      (c.first_name || '').toLowerCase().includes(s) ||
      (c.last_name || '').toLowerCase().includes(s) ||
      (c.name || '').toLowerCase().includes(s);
  });

  const allSelected = filtered.length > 0 && filtered.every(c => selected.has(c.id));
  const someSelected = selected.size > 0;

  const toggleAll = () => {
    if (allSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filtered.map(c => c.id)));
    }
  };

  const toggleOne = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const bulkToggle = async (activate) => {
    const ids = [...selected];
    const toToggle = ids.filter(id => {
      const c = contacts.find(x => x.id === id);
      return c && c.is_active !== activate;
    });
    if (!toToggle.length) { toast.info('Keine Änderungen nötig'); return; }
    let count = 0;
    for (const id of toToggle) {
      try { await onToggle(id); count++; } catch {}
    }
    toast.success(`${count} Kontakte ${activate ? 'aktiviert' : 'deaktiviert'}`);
    setSelected(new Set());
    onRefresh();
  };

  if (loading) return <div className="text-center py-8"><Loader2 className="h-6 w-6 animate-spin text-primary mx-auto" /></div>;
  if (!contacts.length) return <p className="text-sm text-muted-foreground text-center py-8">{isSystem ? 'Keine Mitglieder vorhanden.' : 'Keine Kontakte. E-Mails hinzufügen oder CSV importieren.'}</p>;

  return (
    <div className="space-y-2">
      {/* Search + Bulk Actions */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Suchen..."
            className="rounded-none h-9 text-sm pl-9"
            data-testid="contact-search"
          />
        </div>
        {someSelected && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">{selected.size} ausgewählt</span>
            <Button size="sm" variant="outline" onClick={() => bulkToggle(true)} className="rounded-none h-9 text-xs gap-1" data-testid="bulk-activate-btn">
              <ToggleRight size={14} /> Aktivieren
            </Button>
            <Button size="sm" variant="outline" onClick={() => bulkToggle(false)} className="rounded-none h-9 text-xs gap-1" data-testid="bulk-deactivate-btn">
              <ToggleLeft size={14} /> Deaktivieren
            </Button>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="max-h-[400px] overflow-y-auto overflow-x-auto border border-border">
        <table className="w-full text-sm">
          <thead className="bg-muted/30 sticky top-0 z-10">
            <tr>
              <th className="w-10 p-2 text-center">
                <Checkbox checked={allSelected} onCheckedChange={toggleAll} data-testid="select-all-checkbox" />
              </th>
              <th className="p-2 text-left text-xs text-muted-foreground uppercase tracking-wider font-medium">E-Mail</th>
              <th className="p-2 text-left text-xs text-muted-foreground uppercase tracking-wider font-medium w-[140px]">Vorname</th>
              <th className="p-2 text-left text-xs text-muted-foreground uppercase tracking-wider font-medium w-[140px]">Nachname</th>
              <th className="p-2 text-center text-xs text-muted-foreground uppercase tracking-wider font-medium w-[60px]">Status</th>
              {!isSystem && <th className="w-10"></th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-border/30">
            {filtered.map(c => {
              const isDuplicate = duplicateMap[(c.email || '').toLowerCase()];
              return (
                <tr key={c.id} className={`transition-colors hover:bg-muted/20 ${!c.is_active ? 'opacity-50' : ''} ${selected.has(c.id) ? 'bg-primary/5' : ''}`}>
                  <td className="p-2 text-center">
                    <Checkbox checked={selected.has(c.id)} onCheckedChange={() => toggleOne(c.id)} />
                  </td>
                  <td className="p-2">
                    <div className="flex items-center gap-1.5">
                      <span className="truncate text-sm">{c.email}</span>
                      {isDuplicate && (
                        <span className="flex-shrink-0" title={`Auch in: ${isDuplicate.join(', ')}`}>
                          <Copy size={12} className="text-yellow-500" />
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="p-2 text-sm">{c.first_name || c.vorname || ''}</td>
                  <td className="p-2 text-sm">{c.last_name || c.nachname || c.name || ''}</td>
                  <td className="p-2 text-center">
                    <button
                      onClick={() => { onToggle(c.id); }}
                      className={`inline-flex items-center justify-center w-8 h-8 rounded-none transition-colors ${c.is_active ? 'text-green-500 hover:bg-green-500/10' : 'text-muted-foreground hover:bg-muted'}`}
                      title={c.is_active ? 'Aktiv – Klick zum Deaktivieren' : 'Inaktiv – Klick zum Aktivieren'}
                      data-testid={`toggle-contact-${c.id}`}
                    >
                      {c.is_active ? <ToggleRight size={18} /> : <ToggleLeft size={18} />}
                    </button>
                  </td>
                  {!isSystem && (
                    <td className="p-2 text-center">
                      <button onClick={() => onDelete(c.id)} className="inline-flex items-center justify-center w-8 h-8 text-muted-foreground hover:text-red-500 hover:bg-red-500/10 transition-colors" title="Löschen">
                        <Trash2 size={15} />
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-muted-foreground">{filtered.length} von {contacts.length} Kontakten angezeigt</p>
    </div>
  );
}

export default function NewsletterGroupsPage() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newGroupName, setNewGroupName] = useState('');
  const [expandedGroup, setExpandedGroup] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newFirstName, setNewFirstName] = useState('');
  const [newLastName, setNewLastName] = useState('');
  const [editingGroup, setEditingGroup] = useState(null);
  const [editName, setEditName] = useState('');
  const [csvPreview, setCsvPreview] = useState(null);
  const [allGroupContacts, setAllGroupContacts] = useState({});

  const getToken = () => localStorage.getItem('shrt_token');
  const headers = () => ({ Authorization: `Bearer ${getToken()}` });

  const fetchGroups = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/newsletter-groups`, { headers: headers() });
      setGroups(res.data);
    } catch { toast.error('Fehler beim Laden'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchGroups(); }, [fetchGroups]);

  // Fetch all contacts for duplicate detection
  const fetchAllContacts = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/newsletter-groups`, { headers: headers() });
      const map = {};
      for (const g of res.data) {
        if (g.id === '__all_members__') continue; // skip system group for dedup
        try {
          const cRes = await axios.get(`${API_URL}/api/admin/newsletter-groups/${g.id}/contacts`, { headers: headers() });
          map[g.id] = (cRes.data || []).map(c => ({ ...c, groupName: g.name }));
        } catch {}
      }
      setAllGroupContacts(map);
    } catch {}
  }, []);

  useEffect(() => { fetchAllContacts(); }, [fetchAllContacts]);

  const fetchContacts = async (groupId) => {
    setContactsLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/newsletter-groups/${groupId}/contacts`, { headers: headers() });
      setContacts(res.data);
    } catch { toast.error('Fehler beim Laden der Kontakte'); }
    finally { setContactsLoading(false); }
  };

  const toggleGroup = (groupId) => {
    if (expandedGroup === groupId) {
      setExpandedGroup(null);
      setContacts([]);
    } else {
      setExpandedGroup(groupId);
      fetchContacts(groupId);
    }
  };

  const createGroup = async (e) => {
    e.preventDefault();
    if (!newGroupName.trim()) return;
    try {
      await axios.post(`${API_URL}/api/admin/newsletter-groups`, { name: newGroupName }, { headers: headers() });
      setNewGroupName('');
      fetchGroups();
      toast.success('Gruppe erstellt');
    } catch (err) { toast.error(err.response?.data?.detail || 'Fehler'); }
  };

  const deleteGroup = async (groupId, name) => {
    if (!window.confirm(`Gruppe "${name}" und alle Kontakte löschen?`)) return;
    try {
      await axios.delete(`${API_URL}/api/admin/newsletter-groups/${groupId}`, { headers: headers() });
      if (expandedGroup === groupId) { setExpandedGroup(null); setContacts([]); }
      fetchGroups();
      toast.success('Gruppe gelöscht');
    } catch { toast.error('Fehler beim Löschen'); }
  };

  const toggleGroupActive = async (groupId) => {
    try {
      const res = await axios.patch(`${API_URL}/api/admin/newsletter-groups/${groupId}/toggle`, {}, { headers: headers() });
      setGroups(prev => prev.map(g => g.id === groupId ? { ...g, is_active: res.data.is_active } : g));
    } catch { toast.error('Fehler'); }
  };

  const renameGroup = async (groupId) => {
    if (!editName.trim()) return;
    try {
      await axios.put(`${API_URL}/api/admin/newsletter-groups/${groupId}`, { name: editName }, { headers: headers() });
      setEditingGroup(null);
      fetchGroups();
    } catch { toast.error('Fehler beim Umbenennen'); }
  };

  const addContact = async (e) => {
    e.preventDefault();
    if (!newEmail.trim() || !expandedGroup) return;
    try {
      await axios.post(`${API_URL}/api/admin/newsletter-groups/${expandedGroup}/contacts`, { email: newEmail, first_name: newFirstName, last_name: newLastName }, { headers: headers() });
      setNewEmail(''); setNewFirstName(''); setNewLastName('');
      fetchContacts(expandedGroup);
      fetchGroups();
      fetchAllContacts();
      toast.success('Kontakt hinzugefügt');
    } catch (err) { toast.error(err.response?.data?.detail || 'Fehler'); }
  };

  const deleteContact = async (contactId) => {
    try {
      await axios.delete(`${API_URL}/api/admin/newsletter-groups/${expandedGroup}/contacts/${contactId}`, { headers: headers() });
      setContacts(prev => prev.filter(c => c.id !== contactId));
      fetchGroups();
    } catch { toast.error('Fehler'); }
  };

  const toggleContact = async (contactId) => {
    try {
      const res = await axios.patch(`${API_URL}/api/admin/newsletter-groups/${expandedGroup}/contacts/${contactId}/toggle`, {}, { headers: headers() });
      setContacts(prev => prev.map(c => c.id === contactId ? { ...c, is_active: res.data.is_active } : c));
    } catch { toast.error('Fehler'); }
  };

  const startImport = (groupId) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.csv,.txt';
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (file) setCsvPreview({ file, groupId });
    };
    input.click();
  };

  if (loading) return <Layout><div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div></Layout>;

  return (
    <Layout>
      <div className="space-y-6 max-w-6xl">
        <div className="flex items-center gap-3">
          <Users className="text-primary" size={28} />
          <h1 className="font-['Oswald'] text-2xl md:text-3xl font-bold text-foreground uppercase tracking-tight">
            Versandgruppen
          </h1>
        </div>

        <form onSubmit={createGroup} className="flex gap-2 max-w-lg">
          <Input value={newGroupName} onChange={e => setNewGroupName(e.target.value)} placeholder="Neue Gruppe erstellen..." className="rounded-none" data-testid="new-group-input" />
          <Button type="submit" disabled={!newGroupName.trim()} className="rounded-none bg-primary text-white uppercase text-xs tracking-wider px-5" data-testid="create-group-btn">
            <Plus size={16} className="mr-1" /> Erstellen
          </Button>
        </form>

        <div className="space-y-3">
          {groups.map(group => (
            <div key={group.id} className="border border-border bg-card" data-testid={`group-${group.id}`}>
              <div className="flex items-center gap-3 px-4 py-3">
                <button onClick={() => toggleGroup(group.id)} className="flex-1 flex items-center gap-2 text-left hover:text-primary transition-colors">
                  {expandedGroup === group.id ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                  {group.is_system && <Users size={16} className="text-primary flex-shrink-0" />}
                  <span className="font-semibold text-sm">{group.name}</span>
                  {group.is_system && <span className="text-[10px] bg-primary/20 text-primary px-1.5 py-0.5 uppercase font-medium">Auto</span>}
                  <span className="text-xs text-muted-foreground ml-1">
                    {group.is_system
                      ? `${group.active_contacts} abonniert / ${group.total_contacts} total`
                      : `${group.active_contacts} aktiv${group.total_contacts > group.active_contacts ? ` / ${group.total_contacts} total` : ''}`
                    }
                  </span>
                </button>

                {!group.is_system && (
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Switch checked={group.is_active} onCheckedChange={() => toggleGroupActive(group.id)} data-testid={`toggle-group-${group.id}`} />
                    <Button variant="ghost" size="icon" className="h-9 w-9" onClick={() => { setEditingGroup(group.id); setEditName(group.name); }} title="Umbenennen">
                      <Pencil size={15} />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-9 w-9" onClick={() => startImport(group.id)} title="CSV importieren">
                      <Upload size={15} />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-9 w-9 text-muted-foreground hover:text-red-500" onClick={() => deleteGroup(group.id, group.name)} title="Löschen">
                      <Trash2 size={15} />
                    </Button>
                  </div>
                )}
              </div>

              {/* Edit inline */}
              {editingGroup === group.id && (
                <div className="border-t border-border px-4 py-2 flex items-center gap-2 bg-muted/20">
                  <Input value={editName} onChange={e => setEditName(e.target.value)} className="h-8 rounded-none text-sm max-w-xs" autoFocus onKeyDown={e => e.key === 'Enter' && renameGroup(group.id)} />
                  <Button size="sm" variant="outline" className="rounded-none h-8" onClick={() => renameGroup(group.id)}>
                    <Check size={14} className="mr-1" /> Speichern
                  </Button>
                  <Button size="sm" variant="ghost" className="rounded-none h-8" onClick={() => setEditingGroup(null)}>
                    <X size={14} />
                  </Button>
                </div>
              )}

              {/* Expanded content */}
              {expandedGroup === group.id && (
                <div className="border-t border-border px-4 py-4 space-y-3">
                  {/* Add contact form - custom groups only */}
                  {!group.is_system && (
                    <form onSubmit={addContact} className="flex gap-2 flex-wrap items-end">
                      <div className="flex-1 min-w-[180px]">
                        <label className="text-[10px] text-muted-foreground uppercase tracking-wider mb-0.5 block">E-Mail</label>
                        <Input value={newEmail} onChange={e => setNewEmail(e.target.value)} placeholder="E-Mail" className="rounded-none h-9 text-sm" data-testid="new-contact-email" />
                      </div>
                      <div className="w-36">
                        <label className="text-[10px] text-muted-foreground uppercase tracking-wider mb-0.5 block">Vorname</label>
                        <Input value={newFirstName} onChange={e => setNewFirstName(e.target.value)} placeholder="Vorname" className="rounded-none h-9 text-sm" data-testid="new-contact-firstname" />
                      </div>
                      <div className="w-36">
                        <label className="text-[10px] text-muted-foreground uppercase tracking-wider mb-0.5 block">Nachname</label>
                        <Input value={newLastName} onChange={e => setNewLastName(e.target.value)} placeholder="Nachname" className="rounded-none h-9 text-sm" data-testid="new-contact-lastname" />
                      </div>
                      <Button type="submit" size="sm" disabled={!newEmail.trim()} className="rounded-none h-9 text-xs bg-primary text-white px-4">
                        <Plus size={14} className="mr-1" /> Hinzufügen
                      </Button>
                    </form>
                  )}

                  <ContactTable
                    contacts={contacts}
                    loading={contactsLoading}
                    groupId={group.id}
                    isSystem={group.is_system}
                    onToggle={toggleContact}
                    onDelete={deleteContact}
                    onRefresh={() => { fetchContacts(group.id); fetchGroups(); }}
                    allGroupContacts={allGroupContacts}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {csvPreview && (
        <CsvPreviewDialog
          file={csvPreview.file}
          groupId={csvPreview.groupId}
          onClose={() => setCsvPreview(null)}
          onImported={() => {
            setCsvPreview(null);
            if (expandedGroup === csvPreview.groupId) fetchContacts(csvPreview.groupId);
            fetchGroups();
            fetchAllContacts();
          }}
        />
      )}
    </Layout>
  );
}
